/**
 * Customer / staff / PDF commercial presentation — read-only VoPaymentDetailsSnapshot.
 * Presentation only — does not recalculate commercial authority.
 */

import {
  resolveVoOriginalContractPresentation,
  type VoPaymentDetailsSnapshot,
} from "@shared/variationOrderPaymentDetails";
import { renderVoSignedMoney } from "@shared/rendering";

function money(n: number) {
  return renderVoSignedMoney(n);
}

function pct(n: number) {
  return `${n.toLocaleString("en-SG", { maximumFractionDigits: 2 })}%`;
}

function formatVoDiscount(amount: number, discountPct: number): string {
  if (Math.abs(amount) < 0.004) return money(0);
  const amountPart = `−${money(Math.abs(amount))}`;
  if (Math.abs(discountPct) < 0.004) return amountPart;
  return `${amountPart} (−${pct(Math.abs(discountPct))})`;
}

function Row({
  label,
  value,
  emphasis,
  testId,
  valueClassName,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  testId?: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className={emphasis ? "font-semibold text-[#1A2744]" : "text-muted-foreground"}>{label}</dt>
      <dd
        className={`tabular-nums ${
          emphasis ? "font-semibold text-[#1A2744]" : "font-medium text-[#1A2744]"
        } ${valueClassName ?? ""}`}
        data-testid={testId}
      >
        {value}
      </dd>
    </div>
  );
}

export type VoPriceAdjustmentSectionProps = {
  paymentDetails: VoPaymentDetailsSnapshot;
  /** Current VO number for the "THIS VARIATION ORDER - VO-XXX" heading. */
  voNumber?: string | null;
};

export default function VoPriceAdjustmentSection({
  paymentDetails,
  voNumber,
}: VoPriceAdjustmentSectionProps) {
  const due = Number(paymentDetails.amountDueUponConfirmation ?? 0);
  const originalPresentation = resolveVoOriginalContractPresentation(paymentDetails);
  const alreadyCollected = Number(paymentDetails.alreadyCollectedForRemoved ?? 0);
  const confirmationPct = Number(
    paymentDetails.confirmationPercentage ?? paymentDetails.original?.confirmationPercentage ?? 50,
  );
  const voLabel = String(voNumber ?? "").trim() || "VO";

  return (
    <div className="space-y-6" data-testid="vo-commercial-presentation">
      <section
        className="space-y-3 rounded-xl border border-[#1A2744]/12 bg-[#FAFAF7] p-4"
        data-testid="vo-price-adjustment"
      >
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]">
            Original Contract
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Based on Original Contract</p>
        </div>
        <dl className="space-y-2 text-sm">
          <Row
            label="Original Contract Value"
            value={money(originalPresentation.originalContractValue)}
            testId="vo-price-original-contract"
          />
          <Row
            label="Original Effective Discount"
            value={pct(originalPresentation.originalEffectiveDiscountPct)}
            testId="vo-price-original-discount"
          />
          <Row
            label="Original Agreed Contract Value"
            value={money(originalPresentation.originalAgreedContractValue)}
            testId="vo-price-original-agreed"
          />

          <div className="border-t border-[#1A2744]/12 pt-3 space-y-2">
            <p
              className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]"
              data-testid="vo-price-this-variation-order"
            >
              This Variation Order - {voLabel}
            </p>
            <Row
              label="Added Retail Value"
              value={money(paymentDetails.addedRetailSubtotal)}
              testId="vo-price-added-retail"
            />
            <Row
              label="Variation Order Discount"
              value={formatVoDiscount(
                paymentDetails.voDiscountAmount,
                paymentDetails.voDiscountPct,
              )}
              testId="vo-price-vo-discount"
            />
            <Row
              label="Added Contract Value"
              value={money(paymentDetails.addedContractValue)}
              testId="vo-price-added-contract"
            />
          </div>

          <div className="border-t border-[#1A2744]/15 pt-3">
            <Row
              label="Net Contract Adjustment"
              value={money(paymentDetails.currentVoValue)}
              emphasis
              testId="vo-price-adjustment-net"
            />
          </div>

          <div className="border-t border-[#1A2744]/15 pt-3">
            <Row
              label="Revised Total Contract Value"
              value={money(paymentDetails.revisedContractValue)}
              emphasis
              testId="vo-price-adjustment-revised"
            />
          </div>
        </dl>
      </section>

      <section
        className="space-y-3 rounded-xl border border-[#1A2744]/12 bg-white p-4"
        data-testid="vo-payment-upon-confirmation"
      >
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]">
          Payment Upon Confirmation
        </h2>
        <dl className="space-y-2 text-sm">
          <Row
            label="Added Contract Value"
            value={money(paymentDetails.addedContractValue)}
            testId="vo-payment-added-contract"
          />
          <Row
            label={`Required Deposit (${pct(confirmationPct)})`}
            value={money(paymentDetails.requiredDeposit)}
            testId="vo-payment-required-deposit"
          />
          {alreadyCollected > 0.004 ? (
            <Row
              label="Already Collected for Removed Scope"
              value={money(alreadyCollected)}
              testId="vo-payment-already-collected"
            />
          ) : null}
          <div className="border-t border-[#1A2744]/15 pt-3">
            {Math.abs(due) < 0.004 ? (
              <p
                className="text-sm font-semibold text-[#1A2744]"
                data-testid="vo-price-adjustment-due"
              >
                No Payment Required Upon Confirmation
              </p>
            ) : due < 0 ? (
              <div className="space-y-1">
                <Row
                  label="Credit to Be Applied"
                  value={money(Math.abs(due))}
                  emphasis
                  testId="vo-price-adjustment-due"
                />
                <p className="text-xs text-muted-foreground" data-testid="vo-credit-explanation">
                  This credit will be applied against a future invoice. No cash refund will be issued.
                </p>
              </div>
            ) : (
              <Row
                label="Amount Due Upon Confirmation"
                value={money(due)}
                emphasis
                testId="vo-price-adjustment-due"
              />
            )}
          </div>
        </dl>
      </section>
    </div>
  );
}
